import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Sparkles, Volume2, VolumeX, Check, Leaf } from 'lucide-react';
import { useClickSound } from '../hooks/useClickSound';
import { TOOL_COSTS } from '../hooks/useEnergyManager';
interface AffirmationsProps {
  onClose: () => void;
  onComplete: () => void;
}
type Phase = 'intro' | 'practice' | 'outro';
const AFFIRMATIONS = ['Moje ciało jest bezpiecznym miejscem, a ja jestem w nim w pełni obecna.', 'Z łatwością uwalniam to, co już mi nie służy i robię miejsce na nowe.', 'Mój głos ma znaczenie i wyrażam swoją prawdę z pewnością siebie.', 'Przyciągam obfitość, ponieważ wibruję na paśmie wdzięczności.', 'Każdy mój oddech napełnia mnie spokojem, a wydech uwalnia napięcie.', 'Ufam swojej intuicji; ona zawsze prowadzi mnie w idealnym kierunku.', 'Zasługuję na wszystko co najlepsze, bez konieczności udowadniania swojej wartości.', 'Wybaczam sobie błędy z przeszłości i z czułością patrzę w przyszłość.', 'Moja energia jest chroniona, z łatwością stawiam zdrowe granice.', 'Jestem dokładnie w tym miejscu, w którym powinnam być na mojej drodze.'];
export function Affirmations({
  onClose,
  onComplete
}: AffirmationsProps) {
  const [phase, setPhase] = useState<Phase>('intro');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [direction, setDirection] = useState(1); // 1 for next, -1 for prev
  const {
    playClick,
    playSuccess,
    playBowl
  } = useClickSound();
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  // Speech synthesis helper
  const speak = useCallback((text: string) => {
    if (!('speechSynthesis' in window) || isMuted) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'pl-PL';
    utterance.rate = 0.85;
    utterance.pitch = 1.0;
    const voices = window.speechSynthesis.getVoices();
    const polishVoice = voices.find(v => v.lang.startsWith('pl'));
    if (polishVoice) utterance.voice = polishVoice;
    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  }, [isMuted]);
  // Cleanup speech on unmount
  useEffect(() => {
    return () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);
  // Auto-play speech when index changes in practice phase
  useEffect(() => {
    if (phase === 'practice') {
      // Small delay to let animation finish before speaking
      const timer = setTimeout(() => {
        speak(AFFIRMATIONS[currentIndex]);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, phase, speak]);
  const handleStart = () => {
    playBowl();
    setPhase('practice');
  };
  const handleNext = () => {
    playClick();
    if (navigator.vibrate) navigator.vibrate(30);
    if (currentIndex < AFFIRMATIONS.length - 1) {
      setDirection(1);
      setCurrentIndex(prev => prev + 1);
    } else {
      setPhase('outro');
      playSuccess();
    }
  };
  const handlePrev = () => {
    if (currentIndex > 0) {
      playClick();
      if (navigator.vibrate) navigator.vibrate(30);
      setDirection(-1);
      setCurrentIndex(prev => prev - 1);
    }
  };
  const handleFinish = () => {
    playClick();
    onComplete();
  };
  const handleCloseClick = () => {
    playClick();
    onClose();
  };
  const toggleMute = () => {
    playClick();
    if (!isMuted && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    } else if (isMuted && phase === 'practice') {
      // If unmuting, speak current affirmation
      setTimeout(() => speak(AFFIRMATIONS[currentIndex]), 100);
    }
    setIsMuted(!isMuted);
  };
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0,
      scale: 0.95
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 50 : -50,
      opacity: 0,
      scale: 0.95
    })
  };
  return <motion.div className="fixed inset-0 z-50 flex flex-col w-full h-full overflow-hidden bg-gradient-to-br from-zen-sage to-emerald-900 text-white" initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 20
  }} data-id="element-17">
      {/* Header */}
      <div className="w-full flex items-center justify-between p-4 z-20 shrink-0" data-id="element-18">
        <button onClick={handleCloseClick} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors backdrop-blur-md border border-white/20" data-id="element-19">
          <ArrowLeft className="w-5 h-5" data-id="element-20" />
        </button>
        <div className="flex flex-col items-center" data-id="element-21">
          <span className="text-[10px] text-emerald-200 font-bold tracking-widest uppercase" data-id="element-22">
            Rytuał Skupienia
          </span>
          <span className="text-sm font-medium" data-id="element-23">Afirmacje</span>
        </div>
        <button onClick={toggleMute} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors backdrop-blur-md border border-white/20" data-id="element-24">
          {isMuted ? <VolumeX className="w-4 h-4" data-id="element-25" /> : <Volume2 className="w-4 h-4" data-id="element-26" />}
        </button>
      </div>

      <div className="flex-1 relative flex flex-col items-center justify-center p-6" data-id="element-27">
        <AnimatePresence mode="wait" custom={direction} data-id="element-28">
          {/* INTRO PHASE */}
          {phase === 'intro' && <motion.div key="intro" className="flex flex-col items-center text-center max-w-sm" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.5
        }} data-id="element-29">
              <div className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center mb-8 border border-white/20 shadow-[0_0_40px_rgba(255,255,255,0.1)]" data-id="element-30">
                <Leaf className="w-8 h-8 text-emerald-200" data-id="element-31" />
              </div>
              <h1 className="text-3xl font-light mb-4 leading-tight" data-id="element-32">
                Weź głęboki oddech.
              </h1>
              <p className="text-emerald-100/80 mb-12 text-lg font-light" data-id="element-33">
                Przygotuj się na wgranie
                <br data-id="element-34" />
                nowych kodów.
              </p>
              <button onClick={handleStart} className="w-full bg-white text-emerald-900 rounded-full py-4 text-sm font-bold hover:bg-emerald-50 transition-colors shadow-lg flex items-center justify-center space-x-2" data-id="element-35">
                <span data-id="element-36">Rozpocznij</span>
                <ArrowRight className="w-4 h-4" data-id="element-37" />
              </button>
            </motion.div>}

          {/* PRACTICE PHASE */}
          {phase === 'practice' && <motion.div key="practice" className="flex flex-col items-center w-full h-full" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} data-id="element-38">
              {/* Progress indicator */}
              <div className="absolute top-4 w-full max-w-xs flex justify-center space-x-1.5" data-id="element-39">
                {AFFIRMATIONS.map((_, idx) => <div key={idx} className={`h-1 rounded-full transition-all duration-300 ${idx === currentIndex ? 'w-6 bg-white' : idx < currentIndex ? 'w-2 bg-white/50' : 'w-2 bg-white/20'}`} data-id="element-40" />)}
              </div>

              {/* Breathing instruction */}
              <motion.div className="absolute top-16 text-center" animate={{
            opacity: [0.4, 1, 0.4]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-41">
                <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-200 font-bold" data-id="element-42">
                  Wdech... Powtórz na głos... Wydech...
                </p>
              </motion.div>

              {/* Affirmation Card */}
              <div className="flex-1 flex items-center justify-center w-full relative" data-id="element-43">
                <AnimatePresence mode="popLayout" custom={direction} data-id="element-44">
                  <motion.div key={currentIndex} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
                type: 'spring',
                stiffness: 300,
                damping: 30
              }} className="absolute w-full max-w-sm" data-id="element-45">
                    <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-8 shadow-2xl relative overflow-hidden" data-id="element-46">
                      <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full blur-2xl" data-id="element-47" />
                      <div className="flex justify-center mb-6" data-id="element-48">
                        <Sparkles className="w-6 h-6 text-emerald-200 opacity-50" data-id="element-49" />
                      </div>
                      <h2 className="text-2xl md:text-3xl font-medium text-center leading-relaxed drop-shadow-sm" data-id="element-50">
                        "{AFFIRMATIONS[currentIndex]}"
                      </h2>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation Controls */}
              <div className="w-full max-w-sm flex justify-between items-center mt-auto mb-8 px-4" data-id="element-51">
                <button onClick={handlePrev} disabled={currentIndex === 0} className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center text-white disabled:opacity-30 transition-all hover:bg-white/20 border border-white/20 backdrop-blur-sm" data-id="element-52">
                  <ArrowLeft className="w-6 h-6" data-id="element-53" />
                </button>

                <div className="text-xs font-bold tracking-widest text-emerald-200/70" data-id="element-54">
                  {currentIndex + 1} / {AFFIRMATIONS.length}
                </div>

                <button onClick={handleNext} className="w-14 h-14 rounded-full bg-white text-emerald-900 flex items-center justify-center transition-all hover:scale-105 shadow-lg shadow-emerald-900/20" data-id="element-55">
                  {currentIndex === AFFIRMATIONS.length - 1 ? <Check className="w-6 h-6" data-id="element-56" /> : <ArrowRight className="w-6 h-6" data-id="element-57" />}
                </button>
              </div>
            </motion.div>}

          {/* OUTRO PHASE */}
          {phase === 'outro' && <motion.div key="outro" className="flex flex-col items-center text-center max-w-sm" initial={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} transition={{
          type: 'spring',
          bounce: 0.4
        }} data-id="element-58">
              <div className="w-24 h-24 bg-yellow-400/20 rounded-full flex items-center justify-center mb-6 border-2 border-yellow-400/40 shadow-[0_0_50px_rgba(250,204,21,0.3)] relative" data-id="element-59">
                <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'linear'
            }} className="absolute inset-0 rounded-full border border-yellow-400/30 border-dashed" data-id="element-60" />
                <Sparkles className="w-10 h-10 text-yellow-300" fill="currentColor" data-id="element-61" />
              </div>

              <h2 className="text-3xl font-light mb-2" data-id="element-62">Wibracje podniesione</h2>
              <p className="text-emerald-100/80 mb-8 text-sm" data-id="element-63">
                Nowe kody zostały wgrane do Twojej podświadomości.
              </p>

              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 flex flex-col items-center min-w-[140px] mb-10" data-id="element-64">
                <span className="text-[10px] text-emerald-200 uppercase tracking-widest mb-1 font-bold" data-id="element-65">
                  Koszt Energii
                </span>
                <span className="text-2xl font-bold text-yellow-300 flex items-center" data-id="element-66">
                  {TOOL_COSTS.affirmations}{' '}
                  <Sparkles className="w-5 h-5 ml-1.5" fill="currentColor" data-id="element-67" />
                </span>
              </div>

              <button onClick={handleFinish} className="w-full bg-white text-emerald-900 rounded-full py-4 text-sm font-bold hover:bg-emerald-50 transition-colors shadow-lg" data-id="element-68">
                Odbierz i zakończ
              </button>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
}